/**
 * Request Details Panel
 * Sliding panel that displays detailed information about an application request
 */

import './ScenarioDetailsPanel.css';
import { apiFetch as baseApiFetch } from '../utils/apiClient.js';
import { escapeHtml } from '../utils/html.js';
import { createDeleteConfirmModal } from './ui/DeleteConfirmModal.js';

/**
 * Create request details panel
 * @param {Object} options
 * @param {Object} options.request - Application request to display
 * @param {Function} options.onClose - Callback when panel is closed
 * @param {Function} options.authFetch - Function to make authenticated API calls
 * @param {Function} options.onRequestUpdated - Callback when request is updated (for admin to reload list)
 * @returns {HTMLElement}
 */
export function createRequestDetailsPanel(options = {}) {
  const {
    request,
    onClose,
    authFetch,
    onRequestUpdated,
  } = options;

  const apiFetch = (path, options = {}) => {
    if (typeof authFetch === 'function') {
      return authFetch(path, options);
    }
    return baseApiFetch(path, options);
  };

  const container = document.createElement('div');

  // Overlay (backdrop)
  const overlay = document.createElement('div');
  overlay.className = 'details-panel-overlay';
  overlay.addEventListener('click', handleClose);
  container.appendChild(overlay);

  // Panel
  const panel = document.createElement('div');
  panel.className = 'details-panel';

  // Header
  const header = createHeader();
  panel.appendChild(header);

  // Content (scrollable)
  const content = document.createElement('div');
  content.className = 'details-panel-content';

  // Details section
  const detailsSection = createDetailsSection();
  content.appendChild(detailsSection);

  // Timeline section (messages)
  const timelineSection = createTimelineSection();
  content.appendChild(timelineSection);

  // Reply form section
  const status = request?.status || 'pending';
  if (status !== 'completed') {
    const replyFormSection = createAdminReplyFormSection();
    content.appendChild(replyFormSection);
  }

  panel.appendChild(content);

  // Footer avec actions
  const footer = createFooter();
  panel.appendChild(footer);

  container.appendChild(panel);

  // Activate panel after a brief delay for animation
  setTimeout(() => {
    overlay.classList.add('active');
    panel.classList.add('active');
  }, 10);

  function createHeader() {
    const header = document.createElement('header');
    header.className = 'details-panel-header';

    const top = document.createElement('div');
    top.className = 'details-panel-header-top';
    top.style.cssText = 'display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem;';

    const titleWrapper = document.createElement('div');
    titleWrapper.className = 'details-panel-title-wrapper';
    titleWrapper.innerHTML = `
      <span class="details-panel-icon material-symbols-outlined">description</span>
      <div class="details-panel-title-group">
        <h2 class="details-panel-title">${escapeHtml(request?.name || 'Demande')}</h2>
        <p class="details-panel-subtitle">Détails de votre demande</p>
      </div>
    `;
    top.appendChild(titleWrapper);

    // Conteneur pour le statut et le bouton de fermeture (en haut à droite)
    const rightSection = document.createElement('div');
    rightSection.style.cssText = 'display: flex; align-items: center; gap: 1rem; flex-shrink: 0;';

    // Statut (badge en haut à droite)
    const status = request?.status || 'pending';
    const statusConfig = {
      pending: { text: 'En attente', color: '#b7791f', bg: 'rgba(245, 158, 11, 0.1)', border: 'rgba(245, 158, 11, 0.3)', icon: 'schedule' },
      in_progress: { text: 'En cours', color: '#389798', bg: 'rgba(56, 151, 152, 0.1)', border: 'rgba(56, 151, 152, 0.3)', icon: 'work' },
      completed: { text: 'Terminé', color: '#2e9e6a', bg: 'rgba(46, 158, 106, 0.1)', border: 'rgba(46, 158, 106, 0.3)', icon: 'check_circle' },
    };
    const config = statusConfig[status] || statusConfig.pending;

    const statusBadge = document.createElement('div');
    statusBadge.className = 'request-status-badge';
    statusBadge.dataset.status = status;
    statusBadge.style.cssText = 'display: flex; align-items: center; gap: 0.5rem;';
    statusBadge.innerHTML = `
      <span class="ad-pill" style="--ad-tone-fg: ${config.color}; --ad-tone-bg: ${config.bg};">${config.text}</span>
    `;
    rightSection.appendChild(statusBadge);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'details-panel-close-btn';
    closeBtn.innerHTML = '<span class="material-symbols-outlined">close</span>';
    closeBtn.addEventListener('click', handleClose);
    rightSection.appendChild(closeBtn);

    top.appendChild(rightSection);

    header.appendChild(top);

    // Ajouter les informations type et date de création dans le header
    const headerInfo = document.createElement('div');
    headerInfo.style.cssText = 'display: flex; gap: 1.5rem; align-items: center; margin-top: 16px; padding-top: 16px; border-top: 1px solid rgba(56, 151, 152, 0.1);';

    // Type (badge coloré comme dans la table)
    const requestType = request?.type || 'application';
    const typeConfig = {
      application: { text: 'Application', color: '#389798', bg: 'rgba(56, 151, 152, 0.1)', border: 'rgba(56, 151, 152, 0.3)' },
      command: { text: 'Commande', color: '#25657D', bg: 'rgba(37, 101, 125, 0.1)', border: 'rgba(37, 101, 125, 0.3)' },
      roadmap: { text: 'Suggestion', color: '#29768e', bg: 'rgba(41, 118, 142, 0.1)', border: 'rgba(41, 118, 142, 0.3)' },
    };
    const typeStyle = typeConfig[requestType] || typeConfig.application;

    const typeInfo = document.createElement('div');
    typeInfo.style.cssText = 'display: flex; align-items: center; gap: 0.5rem;';
    typeInfo.innerHTML = `
      <span class="ad-pill ad-pill--plain" style="--ad-tone-fg: ${typeStyle.color}; --ad-tone-bg: ${typeStyle.bg};">${typeStyle.text}</span>
    `;
    headerInfo.appendChild(typeInfo);

    // Date de création
    const dateInfo = document.createElement('div');
    dateInfo.style.cssText = 'display: flex; align-items: center; gap: 0.5rem;';
    const createdAt = request?.createdAt ? new Date(request.createdAt).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }) : 'Non défini';
    dateInfo.innerHTML = `
      <span class="material-symbols-outlined" style="font-size: 18px; color: #508C94;">schedule</span>
      <span style="font-size: 0.875rem; color: var(--text-secondary);">${escapeHtml(createdAt)}</span>
    `;
    headerInfo.appendChild(dateInfo);

    header.appendChild(headerInfo);

    return header;
  }


  // Fonction pour extraire les informations système des notes
  function extractSystemInfo(notes) {
    if (!notes) return null;

    const systemInfo = {};

    // Extraire Système: xxx
    const systemMatch = notes.match(/Système:\s*([^|]+)/i);
    if (systemMatch) {
      systemInfo.system = systemMatch[1].trim();
    }

    // Extraire Plateforme: xxx
    const platformMatch = notes.match(/Plateforme:\s*([^|]+)/i);
    if (platformMatch) {
      systemInfo.platform = platformMatch[1].trim();
    }

    // Extraire Bundle/ID: xxx ou Bundle ID: xxx
    const bundleMatch = notes.match(/Bundle\/?ID:\s*([^\n]+)/i);
    if (bundleMatch) {
      systemInfo.bundleId = bundleMatch[1].trim();
    }

    // Retourner null si aucune info système trouvée
    return Object.keys(systemInfo).length > 0 ? systemInfo : null;
  }

  // Fonction pour retirer les informations système des notes
  function removeSystemInfoFromNotes(notes) {
    if (!notes) return notes;

    // Retirer la ligne avec les infos système (Système: xxx | Plateforme: xxx)
    let cleanNotes = notes.replace(/Système:\s*[^|]+\s*\|\s*Plateforme:\s*[^\n]+/gi, '').trim();

    // Retirer la ligne Bundle/ID: xxx
    cleanNotes = cleanNotes.replace(/Bundle\/?ID:\s*[^\n]+/gi, '').trim();

    // Retirer les séparateurs --- s'ils restent
    cleanNotes = cleanNotes.replace(/^---+\s*/gm, '').trim();
    cleanNotes = cleanNotes.replace(/\n---+\s*/g, '\n').trim();

    // Nettoyer les lignes vides multiples
    cleanNotes = cleanNotes.replace(/\n{3,}/g, '\n\n').trim();

    return cleanNotes || '';
  }

  function createDetailsSection() {
    const section = document.createElement('section');
    section.className = 'details-panel-section';

    const sectionHeader = document.createElement('div');
    sectionHeader.className = 'details-panel-section-header';
    sectionHeader.innerHTML = `
      <h4 class="details-panel-section-title">
        <span class="material-symbols-outlined" style="font-size: 18px; vertical-align: middle; margin-right: 0.5rem;">info</span>
        Détails
      </h4>
    `;
    section.appendChild(sectionHeader);

    const detailsContainer = document.createElement('div');
    detailsContainer.style.cssText = 'padding: 1.25rem; background: var(--bg-secondary); border: 1px solid var(--border-primary); border-radius: 0.5rem;';

    const notes = request?.notes || '';
    const applicationName = request?.applicationName || '';
    const requestType = request?.type || 'application';
    const requestName = request?.name || '';

    let detailsHTML = '';

    // Pour type "application" : mettre l'application en évidence
    if (requestType === 'application') {
      const appName = applicationName || requestName;
      if (appName) {
        detailsHTML += `
          <div style="margin-bottom: 1.5rem; padding: 1rem; background: rgba(56, 151, 152, 0.15); border: 2px solid rgba(56, 151, 152, 0.4); border-radius: 0.5rem;">
            <div style="display: flex; align-items: center; gap: 0.75rem; margin-bottom: 0.5rem;">
              <span class="material-symbols-outlined" style="font-size: 20px; color: #389798;">apps</span>
              <div style="font-size: 0.75rem; color: #389798;  font-weight: 600;">Application demandée</div>
            </div>
            <div style="font-size: 1.1rem; color: var(--text-primary); font-weight: 600;">${escapeHtml(appName)}</div>
          </div>
        `;
      }
    }

    // Pour type "command" : mettre la commande et l'application en évidence
    if (requestType === 'command') {
      detailsHTML += `
        <div style="margin-bottom: 1.5rem; padding: 1rem; background: rgba(37, 101, 125, 0.15); border: 2px solid rgba(37, 101, 125, 0.4); border-radius: 0.5rem;">
          <div style="display: flex; align-items: center; gap: 0.75rem; margin-bottom: 0.75rem;">
            <span class="material-symbols-outlined" style="font-size: 20px; color: #25657D;">terminal</span>
            <div style="font-size: 0.75rem; color: #25657D;  font-weight: 600;">Commande demandée</div>
          </div>
          <div style="font-size: 1.1rem; color: var(--text-primary); font-weight: 600; margin-bottom: 1rem;">${escapeHtml(requestName)}</div>
          ${applicationName ? `
            <div style="padding-top: 1rem; border-top: 1px solid rgba(37, 101, 125, 0.3);">
              <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem;">
                <span class="material-symbols-outlined" style="font-size: 18px; color: #25657D;">apps</span>
                <div style="font-size: 0.75rem; color: #25657D;  font-weight: 600;">Application</div>
              </div>
              <div style="font-size: 0.95rem; color: var(--text-primary); font-weight: 500;">${escapeHtml(applicationName)}</div>
            </div>
          ` : ''}
        </div>
      `;
    }

    // Extraire les informations système des notes
    const systemInfo = extractSystemInfo(notes);
    const cleanNotes = systemInfo ? removeSystemInfoFromNotes(notes) : notes;

    // Afficher les informations système si présentes
    if (systemInfo) {
      detailsHTML += `
        <div style="margin-bottom: 1.5rem; padding: 1rem; background: var(--border-secondary); border: 1px solid rgba(80, 140, 148, 0.3); border-radius: 0.5rem;">
          <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.75rem;">
            <span class="material-symbols-outlined" style="font-size: 18px; color: #508C94;">computer</span>
            <div style="font-size: 0.75rem; color: #508C94;  font-weight: 600;">Informations système</div>
          </div>
          <div style="display: flex; flex-wrap: wrap; gap: 1rem;">
            ${systemInfo.system ? `
              <div style="display: flex; align-items: center; gap: 0.5rem;">
                <span class="material-symbols-outlined" style="font-size: 16px; color: #508C94;">desktop_windows</span>
                <div>
                  <div style="font-size: 0.65rem; color: #508C94;  margin-bottom: 0.25rem;">Système</div>
                  <div style="font-size: 0.875rem; color: #2e9e6a; font-weight: 500;">${escapeHtml(systemInfo.system)}</div>
                </div>
              </div>
            ` : ''}
            ${systemInfo.platform ? `
              <div style="display: flex; align-items: center; gap: 0.5rem;">
                <span class="material-symbols-outlined" style="font-size: 16px; color: #508C94;">memory</span>
                <div>
                  <div style="font-size: 0.65rem; color: #508C94;  margin-bottom: 0.25rem;">Plateforme</div>
                  <div style="font-size: 0.875rem; color: #2e9e6a; font-weight: 500;">${escapeHtml(systemInfo.platform)}</div>
                </div>
              </div>
            ` : ''}
            ${systemInfo.bundleId ? `
              <div style="display: flex; align-items: center; gap: 0.5rem;">
                <span class="material-symbols-outlined" style="font-size: 16px; color: #508C94;">tag</span>
                <div>
                  <div style="font-size: 0.65rem; color: #508C94;  margin-bottom: 0.25rem;">Bundle ID</div>
                  <div style="font-size: 0.875rem; color: #2e9e6a; font-weight: 500; font-family: 'JetBrains Mono', monospace;">${escapeHtml(systemInfo.bundleId)}</div>
                </div>
              </div>
            ` : ''}
          </div>
        </div>
      `;
    }

    // Notes
    detailsHTML += `
      <div>
        <div style="font-size: 0.75rem; color: #508C94;  margin-bottom: 0.5rem;">Notes</div>
        <div style="font-size: 0.95rem; color: var(--text-primary); line-height: 1.6; white-space: pre-wrap;">${cleanNotes ? escapeHtml(cleanNotes) : '<span style="color: #508C94; font-style: italic;">Aucune note</span>'}</div>
      </div>
    `;

    detailsContainer.innerHTML = detailsHTML;
    section.appendChild(detailsContainer);

    return section;
  }

  function createTimelineSection() {
    const section = document.createElement('section');
    section.className = 'details-panel-section';

    const sectionHeader = document.createElement('div');
    sectionHeader.className = 'details-panel-section-header';
    sectionHeader.innerHTML = `
      <h4 class="details-panel-section-title">
        <span class="material-symbols-outlined" style="font-size: 18px; vertical-align: middle; margin-right: 0.5rem;">history</span>
        Chronologie
      </h4>
    `;
    section.appendChild(sectionHeader);

    // Créer un conteneur pour la timeline
    const timelineContainer = document.createElement('div');
    timelineContainer.style.cssText = 'position: relative; padding-left: 80px;';

    // Créer la ligne verticale de la timeline
    const timelineLine = document.createElement('div');
    timelineLine.style.cssText = 'position: absolute; left: 40px; top: 0; bottom: 0; width: 2px; background: var(--border-primary); z-index: 1;';
    timelineContainer.appendChild(timelineLine);

    // Créer un conteneur pour les items
    const itemsContainer = document.createElement('div');
    itemsContainer.style.cssText = 'position: relative; z-index: 2; display: flex; flex-direction: column; gap: 1.5rem;';

    // Collecter tous les événements dans l'ordre chronologique
    const events = [];

    // Demande initiale
    if (request?.createdAt) {
      events.push({
        type: 'initial',
        date: new Date(request.createdAt),
        name: request.name,
        notes: request.notes,
      });
    }

    // Messages (dédupliqués côté API, on affiche simplement)
    if (request?.messages && Array.isArray(request.messages)) {
      request.messages.forEach(msg => {
        const messageId = msg._id?.toString() || msg.id?.toString();

        events.push({
          type: 'message',
          date: new Date(msg.createdAt),
          authorType: msg.authorType,
          authorName: msg.authorName || (msg.authorType === 'admin' ? 'Support' : 'Vous'),
          message: msg.message,
          messageId: messageId,
        });
      });
    }

    // Trier par date
    events.sort((a, b) => a.date - b.date);

    // Rendre les événements
    if (events.length === 0) {
      itemsContainer.innerHTML = `
        <div style="padding: 1rem; text-align: center; color: #508C94; font-size: 0.875rem;">
          Aucun événement pour le moment
        </div>
      `;
    } else {
      events.forEach((event, index) => {
        const item = document.createElement('div');
        item.style.cssText = 'display: flex; gap: 1rem; align-items: flex-start;';

        // Icône
        const icon = document.createElement('div');
        icon.style.cssText = 'width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0;';

        if (event.type === 'initial') {
          icon.style.cssText += 'background: rgba(56, 151, 152, 0.2); border: 2px solid rgba(56, 151, 152, 0.4);';
          icon.innerHTML = '<span class="material-symbols-outlined" style="font-size: 18px; color: #389798;">add_circle</span>';
        } else if (event.type === 'message') {
          if (event.authorType === 'admin') {
            icon.style.cssText += 'background: rgba(56, 151, 152, 0.2); border: 2px solid rgba(56, 151, 152, 0.4);';
            icon.innerHTML = '<span class="material-symbols-outlined" style="font-size: 18px; color: #389798;">support_agent</span>';
          } else {
            icon.style.cssText += 'background: rgba(46, 158, 106, 0.2); border: 2px solid rgba(46, 158, 106, 0.4);';
            icon.innerHTML = '<span class="material-symbols-outlined" style="font-size: 18px; color: #2e9e6a;">person</span>';
          }
        }

        item.appendChild(icon);

        // Content wrapper avec position relative pour la date/heure à droite
        const contentWrapper = document.createElement('div');
        contentWrapper.style.cssText = 'flex: 1; min-width: 0; position: relative; padding-right: 120px;';

        // Time avec date et heure à droite au-dessus de la card
        const time = document.createElement('div');
        time.style.cssText = 'position: absolute; right: 0; top: 0; display: flex; flex-direction: column; gap: 0.1rem; align-items: flex-end;';
        const dateStr = event.date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
        const timeStr = event.date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        time.innerHTML = `
          <span style="font-family: 'JetBrains Mono', monospace; font-size: 0.65rem; color: rgba(80, 140, 148, 0.8);  line-height: 1.2;">${escapeHtml(dateStr)}</span>
          <span style="font-family: 'JetBrains Mono', monospace; font-size: 0.65rem; color: rgba(80, 140, 148, 0.8);  line-height: 1.2;">${escapeHtml(timeStr)}</span>
        `;
        contentWrapper.appendChild(time);

        // Contenu
        const content = document.createElement('div');
        content.style.cssText = 'flex: 1; min-width: 0;';

        if (event.type === 'initial') {
          content.innerHTML = `
            <div style="font-size: 0.875rem; font-weight: 600; color: var(--text-primary); margin-bottom: 0.5rem;">Demande créée</div>
            <div style="font-size: 0.875rem; color: var(--text-primary); font-weight: 500; margin-bottom: 0.25rem;">${escapeHtml(event.name)}</div>
            ${event.notes ? `<div style="font-size: 0.875rem; color: var(--text-secondary); line-height: 1.6; white-space: pre-wrap;">${escapeHtml(event.notes)}</div>` : ''}
          `;
        } else if (event.type === 'message') {
          const authorColor = event.authorType === 'admin' ? '#389798' : '#2e9e6a';
          const messageId = event.messageId || event.id || event._id;
          const deleteButton = event.authorType === 'admin' && messageId ? `
            <button class="btn-icon admin-message-delete" type="button" title="Supprimer" data-request-id="${request?.id || request?._id || ''}" data-message-id="${messageId}" style="background: transparent; border: none; color: rgba(248, 113, 113, 0.8); cursor: pointer; padding: 0.25rem; font-size: 14px; line-height: 1;">🗑️</button>
          ` : '';
          content.innerHTML = `
            <div style="font-size: 0.875rem; font-weight: 600; color: ${authorColor}; margin-bottom: 0.5rem; display: flex; align-items: center; gap: 0.5rem;">
              <span>${escapeHtml(event.authorName)}</span>
              ${deleteButton}
            </div>
            <div style="font-size: 0.875rem; color: var(--text-primary); line-height: 1.6; white-space: pre-wrap;">${escapeHtml(event.message)}</div>
          `;
        }

        contentWrapper.appendChild(content);
        item.appendChild(contentWrapper);
        itemsContainer.appendChild(item);
      });
    }

    timelineContainer.appendChild(itemsContainer);
    section.appendChild(timelineContainer);

    return section;
  }

  function createAdminReplyFormSection() {
    const section = document.createElement('section');
    section.className = 'details-panel-section';

    const sectionHeader = document.createElement('div');
    sectionHeader.className = 'details-panel-section-header';
    sectionHeader.innerHTML = `
      <h4 class="details-panel-section-title">
        <span class="material-symbols-outlined" style="font-size: 18px; vertical-align: middle; margin-right: 0.5rem;">reply</span>
        Répondre en mode support
      </h4>
    `;
    section.appendChild(sectionHeader);

    const formContainer = document.createElement('div');
    formContainer.style.cssText = 'padding: 1.25rem; background: var(--bg-secondary); border: 1px solid var(--border-primary); border-radius: 0.5rem;';

    const feedback = document.createElement('div');
    feedback.id = 'request-message-feedback';
    feedback.style.cssText = 'display: none; padding: 0.75rem 1rem; margin-bottom: 1rem; border-radius: 0.5rem; font-size: 0.875rem;';
    formContainer.appendChild(feedback);

    const form = document.createElement('form');
    form.id = 'request-message-form';
    form.dataset.requestId = request?.id || request?._id || '';

    const textarea = document.createElement('textarea');
    textarea.id = 'request-message-textarea';
    textarea.placeholder = 'Tapez votre réponse ici...';
    textarea.maxLength = 5000;
    textarea.rows = 6;
    textarea.style.cssText = 'width: 100%; padding: 0.75rem; background: var(--bg-input); border: 2px solid var(--border-primary); border-radius: 0.5rem; color: var(--text-primary); font-family: inherit; font-size: 0.95rem; line-height: 1.6; resize: vertical; min-height: 120px; margin-bottom: 1rem;';
    form.appendChild(textarea);

    const buttonContainer = document.createElement('div');
    buttonContainer.style.cssText = 'display: flex; gap: 0.75rem; align-items: center;';

    const submitBtn = document.createElement('button');
    submitBtn.type = 'submit';
    submitBtn.id = 'request-message-submit-btn';
    submitBtn.className = 'cmd-center-btn-primary';
    submitBtn.style.cssText = 'flex: 0 0 auto;';
    submitBtn.innerHTML = `
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align: middle; margin-right: 0.5rem;">
        <polyline points="20 6 9 17 4 12"></polyline>
      </svg>
      <span>Envoyer la réponse</span>
    `;
    buttonContainer.appendChild(submitBtn);

    const statusSpan = document.createElement('span');
    statusSpan.id = 'request-message-status';
    statusSpan.style.cssText = 'font-size: 0.75rem; color: #508C94; margin-left: auto;';
    buttonContainer.appendChild(statusSpan);

    form.appendChild(buttonContainer);
    formContainer.appendChild(form);
    section.appendChild(formContainer);

    // Event listener pour l'envoi du message
    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const requestId = form.dataset.requestId;
      const message = textarea.value.trim();

      if (!requestId) {
        if (feedback) {
          feedback.style.display = 'block';
          feedback.className = 'request-feedback error';
          feedback.textContent = 'Erreur: ID de demande manquant';
        }
        return;
      }

      if (!message) {
        if (feedback) {
          feedback.style.display = 'block';
          feedback.className = 'request-feedback error';
          feedback.textContent = 'Veuillez saisir un message.';
        }
        textarea.focus();
        return;
      }

      // Désactiver le bouton pendant l'envoi
      submitBtn.disabled = true;
      submitBtn.innerHTML = '<span class="loading-spinner-small"></span><span>Envoi…</span>';

      if (statusSpan) {
        statusSpan.textContent = 'Envoi en cours...';
        statusSpan.style.color = '#508C94';
      }

      if (feedback) {
        feedback.style.display = 'block';
        feedback.className = 'request-feedback info';
        feedback.textContent = 'Envoi en cours...';
      }

      try {
        const response = await apiFetch(`/api/admin/requests/${requestId}/messages`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData?.error || 'Impossible d\'enregistrer le message');
        }

        // Vider le textarea
        textarea.value = '';

        // Afficher la belle modal de succès
        if (typeof window.showSuccessModal === 'function') {
          window.showSuccessModal('Message envoyé avec succès.', 'Message envoyé');
        }

        // L'API retourne seulement le nouveau message, pas toute la demande
        // Il faut recharger la demande complète depuis l'API
        // requestId est déjà défini plus haut (ligne 627)
        try {
          const requestResponse = await apiFetch(`/api/admin/requests/${requestId}`);
          if (requestResponse.ok) {
            const updatedRequest = await requestResponse.json();
            if (updatedRequest) {
              // Mettre à jour la demande dans le panel avec les messages dédupliqués de l'API
              if (updatedRequest.messages) {
                request.messages = updatedRequest.messages;
              }
              // Mettre à jour le statut si il a changé
              if (updatedRequest.status && updatedRequest.status !== request.status) {
                request.status = updatedRequest.status;
                // Mettre à jour le badge de statut dans le header
                const statusBadge = panel.querySelector('.request-status-badge');
                if (statusBadge) {
                  const statusConfig = {
                    pending: { text: 'En attente', color: '#b7791f', bg: 'rgba(245, 158, 11, 0.1)', border: 'rgba(245, 158, 11, 0.3)', icon: 'schedule' },
                    in_progress: { text: 'En cours', color: '#389798', bg: 'rgba(56, 151, 152, 0.1)', border: 'rgba(56, 151, 152, 0.3)', icon: 'work' },
                    completed: { text: 'Terminé', color: '#2e9e6a', bg: 'rgba(46, 158, 106, 0.1)', border: 'rgba(46, 158, 106, 0.3)', icon: 'check_circle' },
                  };
                  const config = statusConfig[updatedRequest.status] || statusConfig.pending;
                  statusBadge.dataset.status = updatedRequest.status;
                  statusBadge.innerHTML = `
                    <span class="ad-pill" style="--ad-tone-fg: ${config.color}; --ad-tone-bg: ${config.bg};">${config.text}</span>
                  `;
                }
              }
            }
          }
        } catch (error) {
          console.error('Error reloading request after message send', error);
        }

        // Recharger la timeline
        const timelineSections = content.querySelectorAll('.details-panel-section');
        let oldTimeline = null;
        for (const section of timelineSections) {
          const header = section.querySelector('.details-panel-section-header');
          if (header && header.textContent.includes('Chronologie')) {
            oldTimeline = section;
            break;
          }
        }
        if (oldTimeline) {
          const newTimeline = createTimelineSection();
          oldTimeline.replaceWith(newTimeline);

          // Réattacher les event listeners pour la suppression de messages
          panel.querySelectorAll('.admin-message-delete').forEach((btn) => {
            btn.addEventListener('click', handleAdminMessageDelete);
          });
        }

        // Callback pour recharger la liste si fourni
        if (onRequestUpdated && typeof onRequestUpdated === 'function') {
          onRequestUpdated();
        }
      } catch (error) {
        console.error('Error sending request message', error);
        if (statusSpan) {
          statusSpan.textContent = 'Erreur lors de l\'envoi: ' + (error.message || 'Erreur inconnue');
          statusSpan.style.color = '#c9453a';
        }

        if (feedback) {
          feedback.className = 'request-feedback error';
          feedback.textContent = error.message || 'Erreur lors de l\'envoi du message.';
        }

        if (typeof window.showGlobalNotification === 'function') {
          window.showGlobalNotification('error', 'Erreur lors de l\'envoi du message');
        }
      } finally {
        // Réactiver le bouton
        submitBtn.disabled = false;
        submitBtn.innerHTML = `
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align: middle; margin-right: 0.5rem;">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          <span>Envoyer la réponse</span>
        `;
      }
    });

    return section;
  }

  function createFooter() {
    const footer = document.createElement('footer');
    footer.className = 'details-panel-footer';
    footer.style.cssText = 'padding: 1rem 1.5rem; border-top: 1px solid rgba(56, 151, 152, 0.1); display: flex; justify-content: flex-start; align-items: center; gap: 0.75rem;';

    const status = request?.status || 'pending';
    const isCompleted = status === 'completed';

    // Actions pour user et admin
    if (!isCompleted) {
      // Bouton Clôturer (user et admin)
      const closeRequestBtn = document.createElement('button');
      closeRequestBtn.className = 'cmd-center-btn-primary';
      closeRequestBtn.style.cssText = 'display: flex; align-items: center; gap: 0.5rem;';
      closeRequestBtn.innerHTML = `
        <span class="material-symbols-outlined" style="font-size: 18px;">check_circle</span>
        <span>Clôturer</span>
      `;
      closeRequestBtn.addEventListener('click', async () => {
        await handleCloseRequest();
      });
      footer.appendChild(closeRequestBtn);
    }

    // Bouton Changer le statut
    const changeStatusBtn = document.createElement('button');
    changeStatusBtn.className = 'cmd-center-btn-secondary';
    changeStatusBtn.style.cssText = 'display: flex; align-items: center; gap: 0.5rem;';
    changeStatusBtn.innerHTML = `
      <span class="material-symbols-outlined" style="font-size: 18px;">swap_horiz</span>
      <span>Changer le statut</span>
    `;
    changeStatusBtn.addEventListener('click', () => {
      showChangeStatusModal();
    });
    footer.appendChild(changeStatusBtn);

    // Bouton Supprimer
    const deleteRequestBtn = document.createElement('button');
    deleteRequestBtn.className = 'cmd-center-btn-danger';
    deleteRequestBtn.style.cssText = 'display: flex; align-items: center; gap: 0.5rem;';
    deleteRequestBtn.innerHTML = `
      <span class="material-symbols-outlined" style="font-size: 18px;">delete</span>
      <span>Supprimer</span>
    `;
    deleteRequestBtn.addEventListener('click', async () => {
      await handleDeleteRequest();
    });
    footer.appendChild(deleteRequestBtn);

    return footer;
  }

  async function handleCloseRequest() {
    const requestId = request?.id || request?._id;
    if (!requestId) return;

    if (!confirm('Êtes-vous sûr de vouloir clôturer cette demande ?')) {
      return;
    }

    try {
      const response = await apiFetch(`/api/applications/requests/${requestId}/close`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Erreur lors de la clôture de la demande');
      }

      // Callback pour recharger la liste si fourni
      if (onRequestUpdated && typeof onRequestUpdated === 'function') {
        onRequestUpdated();
      }

      if (typeof window.showSuccessModal === 'function') {
        window.showSuccessModal('La demande a été clôturée avec succès.', 'Demande clôturée', () => {
          handleClose();
        });
      } else {
        handleClose();
      }
    } catch (error) {
      console.error('Error closing request', error);
      if (typeof window.showGlobalNotification === 'function') {
        window.showGlobalNotification('error', error.message || 'Erreur lors de la clôture de la demande');
      } else {
        alert(error.message || 'Erreur lors de la clôture de la demande');
      }
    }
  }

  async function handleDeleteRequest() {
    const requestId = request?.id || request?._id;
    if (!requestId) return;

    const deleteModal = createDeleteConfirmModal({
      title: 'Supprimer la demande',
      message: 'Êtes-vous sûr de vouloir supprimer cette demande ?',
      warningMessage: 'Cette action est <strong>irréversible</strong>.',
      confirmText: 'Supprimer définitivement',
      cancelText: 'Annuler',
      onConfirm: async () => {
        try {
          const response = await apiFetch(`/api/admin/requests/${requestId}`, {
            method: 'DELETE',
          });

          if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.error || 'Erreur lors de la suppression de la demande');
          }

          // Callback pour recharger la liste si fourni
          if (onRequestUpdated && typeof onRequestUpdated === 'function') {
            onRequestUpdated();
          }

          if (typeof window.showSuccessModal === 'function') {
            window.showSuccessModal('La demande a été supprimée avec succès.', 'Demande supprimée', () => {
              handleClose();
            });
          } else {
            handleClose();
          }
        } catch (error) {
          console.error('Error deleting request', error);
          if (typeof window.showGlobalNotification === 'function') {
            window.showGlobalNotification('error', error.message || 'Erreur lors de la suppression de la demande');
          } else {
            alert(error.message || 'Erreur lors de la suppression de la demande');
          }
        }
      },
    });
    
    deleteModal.show();
  }

  function handleClose() {
    overlay.classList.remove('active');
    panel.classList.remove('active');
    setTimeout(() => {
      if (onClose) {
        onClose();
      }
      container.remove();
    }, 300);
  }

  // Close on Escape key
  const escapeHandler = (e) => {
    if (e.key === 'Escape') {
      handleClose();
      document.removeEventListener('keydown', escapeHandler);
    }
  };
  document.addEventListener('keydown', escapeHandler);

  // Handler pour la suppression de messages (admin uniquement)
  async function handleAdminMessageDelete(e) {
    const button = e.target.closest('button');
    if (!button) return;

    const requestId = button.dataset.requestId;
    const messageId = button.dataset.messageId;

    if (!requestId || !messageId) {
      console.error('Missing requestId or messageId');
      return;
    }

    const deleteModal = createDeleteConfirmModal({
      title: 'Supprimer le message',
      message: 'Êtes-vous sûr de vouloir supprimer ce message ?',
      warningMessage: 'Cette action est <strong>irréversible</strong>.',
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      onConfirm: async () => {
        try {
          button.disabled = true;
          const response = await apiFetch(`/api/admin/requests/${requestId}/messages/${messageId}`, {
            method: 'DELETE',
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData?.error || 'Impossible de supprimer le message');
          }

          // Recharger la timeline
          const timelineSections = content.querySelectorAll('.details-panel-section');
          let oldTimeline = null;
          for (const section of timelineSections) {
            const header = section.querySelector('.details-panel-section-header');
            if (header && header.textContent.includes('Chronologie')) {
              oldTimeline = section;
              break;
            }
          }
          if (oldTimeline) {
            // Recharger les données de la demande
            const updatedResponse = await apiFetch(`/api/admin/requests/${requestId}`);
            if (updatedResponse.ok) {
              const updatedRequest = await updatedResponse.json();
              if (updatedRequest) {
                request.messages = updatedRequest.messages || request.messages || [];
                const newTimeline = createTimelineSection();
                oldTimeline.replaceWith(newTimeline);

                // Réattacher les event listeners
                panel.querySelectorAll('.admin-message-delete').forEach((btn) => {
                  btn.addEventListener('click', handleAdminMessageDelete);
                });
              }
            }
          }

          // Callback pour recharger la liste si fourni
          if (onRequestUpdated && typeof onRequestUpdated === 'function') {
            onRequestUpdated();
          }

          if (typeof window.showSuccessModal === 'function') {
            window.showSuccessModal('Message supprimé avec succès.', 'Message supprimé');
          }
        } catch (error) {
          console.error('Error deleting message', error);
          if (typeof window.showGlobalNotification === 'function') {
            window.showGlobalNotification('error', error.message || 'Erreur lors de la suppression du message');
          }
        } finally {
          button.disabled = false;
        }
      },
    });
    
    deleteModal.show();
  }

  // Attacher les event listeners pour la suppression de messages
  setTimeout(() => {
    panel.querySelectorAll('.admin-message-delete').forEach((btn) => {
      btn.addEventListener('click', handleAdminMessageDelete);
    });
  }, 100);

  function showChangeStatusModal() {
    const modal = document.createElement('div');
    modal.className = 'modal modal-validate-confirm';
    modal.style.cssText = 'display: flex; position: fixed; inset: 0; background-color: rgba(0, 0, 0, 0.7); backdrop-filter: blur(8px); align-items: center; justify-content: center; z-index: 3000;';

    const currentStatus = request?.status || 'pending';
    const statusOptions = [
      { value: 'pending', label: 'En attente', color: '#b7791f', bg: 'rgba(245, 158, 11, 0.1)', border: 'rgba(245, 158, 11, 0.3)', icon: 'schedule' },
      { value: 'in_progress', label: 'En cours', color: '#389798', bg: 'rgba(56, 151, 152, 0.1)', border: 'rgba(56, 151, 152, 0.3)', icon: 'work' },
      { value: 'completed', label: 'Terminé', color: '#2e9e6a', bg: 'rgba(46, 158, 106, 0.1)', border: 'rgba(46, 158, 106, 0.3)', icon: 'check_circle' },
    ];

    let selectedStatus = currentStatus;

    modal.innerHTML = `
      <div class="modal-content modal-validate-content" style="max-width: 520px; width: 90%;">
        <div class="modal-validate-body">
          <div class="modal-validate-content-wrapper">
            <h2 class="modal-validate-title">
              Changer le statut de la demande
            </h2>
            <div style="margin-bottom: 1.5rem;">
              <label class="ad-label" style="display: block; margin-bottom: 0.75rem;">
                Sélectionner un statut
              </label>
              <div style="display: flex; flex-direction: column; gap: 0.75rem;">
                ${statusOptions.map(opt => {
      const isSelected = opt.value === currentStatus;
      return `
                    <button 
                      type="button"
                      class="status-option-btn" 
                      data-status="${opt.value}"
                      ${isSelected ? 'data-selected="true"' : ''}
                      style="
                        width: 100%;
                        padding: 1rem 1.25rem;
                        background: ${isSelected ? opt.bg : 'rgba(56, 151, 152, 0.05)'};
                        border: 2px solid ${isSelected ? opt.border : 'rgba(56, 151, 152, 0.2)'};
                        border-radius: 0.5rem;
                        color: ${opt.color};
                        font-size: 0.95rem;
                        font-family: 'Barlow', sans-serif;
                        font-weight: 600;
                        cursor: pointer;
                        transition: all 0.3s ease;
                        display: flex;
                        align-items: center;
                        gap: 1rem;
                        text-align: left;
                        outline: none;
                        ${isSelected ? `box-shadow: 0 0 20px ${opt.color}40;` : ''}
                      "
                    >
                      <span class="material-symbols-outlined" style="font-size: 24px; color: ${opt.color}; flex-shrink: 0;">
                        ${opt.icon}
                      </span>
                      <div style="flex: 1;">
                        <div style="font-size: 1rem; font-weight: 600; margin-bottom: 0.25rem;">${escapeHtml(opt.label)}</div>
                        ${isSelected ? '<div style="font-size: 0.75rem; opacity: 0.8;">Statut actuel</div>' : ''}
                      </div>
                      ${isSelected ? `
                        <span class="material-symbols-outlined" style="font-size: 20px; color: ${opt.color}; flex-shrink: 0;">
                          check_circle
                        </span>
                      ` : ''}
                    </button>
                  `;
    }).join('')}
              </div>
            </div>
          </div>
        </div>
        <div class="modal-validate-footer" style="display: flex; gap: 0.75rem;">
          <button class="modal-validate-btn modal-validate-btn-cancel">
            <span>Annuler</span>
          </button>
          <button class="modal-validate-btn modal-validate-btn-confirm">
            <span class="material-symbols-outlined" style="font-size: 18px;">check</span>
            <span>Confirmer</span>
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    // Animation d'entrée
    setTimeout(() => {
      modal.classList.add('active');
    }, 10);

    const statusButtons = modal.querySelectorAll('.status-option-btn');
    const confirmBtn = modal.querySelector('.modal-validate-btn-confirm');
    const cancelBtn = modal.querySelector('.modal-validate-btn-cancel');

    // Gérer la sélection des statuts
    statusButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        // Désélectionner tous les boutons
        statusButtons.forEach(b => {
          b.style.background = 'rgba(56, 151, 152, 0.05)';
          b.style.borderColor = 'rgba(56, 151, 152, 0.2)';
          b.style.boxShadow = 'none';
          b.dataset.selected = 'false';
          const checkIcon = b.querySelector('.material-symbols-outlined:last-child');
          if (checkIcon && checkIcon.textContent === 'check_circle') {
            checkIcon.remove();
          }
          const statusText = b.querySelector('div > div:last-child');
          if (statusText && statusText.textContent === 'Statut actuel') {
            statusText.remove();
          }
        });

        // Sélectionner le bouton cliqué
        const statusValue = btn.dataset.status;
        const opt = statusOptions.find(o => o.value === statusValue);
        selectedStatus = statusValue;
        btn.style.background = opt.bg;
        btn.style.borderColor = opt.border;
        btn.style.boxShadow = `0 0 20px ${opt.color}40`;
        btn.dataset.selected = 'true';

        // Ajouter l'icône de check si ce n'est pas le statut actuel
        if (statusValue !== currentStatus) {
          const checkIcon = document.createElement('span');
          checkIcon.className = 'material-symbols-outlined';
          checkIcon.style.cssText = `font-size: 20px; color: ${opt.color}; flex-shrink: 0;`;
          checkIcon.textContent = 'check_circle';
          btn.appendChild(checkIcon);
        }
      });
    });

    const closeModal = () => {
      modal.classList.remove('active');
      setTimeout(() => {
        modal.remove();
      }, 300);
    };

    cancelBtn.addEventListener('click', closeModal);
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        closeModal();
      }
    });

    confirmBtn.addEventListener('click', async () => {
      if (selectedStatus === currentStatus) {
        closeModal();
        return;
      }

      confirmBtn.disabled = true;
      const originalHtml = confirmBtn.innerHTML;
      confirmBtn.innerHTML = '<span class="loading-spinner-small"></span><span>Mise à jour...</span>';

      try {
        const requestId = request?.id || request?._id;
        if (!requestId) {
          throw new Error('ID de demande manquant');
        }

        const response = await apiFetch(`/api/admin/requests/${requestId}/status`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: selectedStatus }),
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.error || 'Erreur lors de la mise à jour du statut');
        }

        const updatedRequest = await response.json();

        // Mettre à jour le statut dans l'objet request
        request.status = updatedRequest.status;

        // Mettre à jour le badge de statut dans le header
        const statusBadge = panel.querySelector('.request-status-badge');
        if (statusBadge) {
          const statusConfig = {
            pending: { text: 'En attente', color: '#b7791f', bg: 'rgba(245, 158, 11, 0.1)', border: 'rgba(245, 158, 11, 0.3)', icon: 'schedule' },
            in_progress: { text: 'En cours', color: '#389798', bg: 'rgba(56, 151, 152, 0.1)', border: 'rgba(56, 151, 152, 0.3)', icon: 'work' },
            completed: { text: 'Terminé', color: '#2e9e6a', bg: 'rgba(46, 158, 106, 0.1)', border: 'rgba(46, 158, 106, 0.3)', icon: 'check_circle' },
          };
          const config = statusConfig[updatedRequest.status] || statusConfig.pending;
          statusBadge.dataset.status = updatedRequest.status;
          statusBadge.innerHTML = `
            <span class="ad-pill" style="--ad-tone-fg: ${config.color}; --ad-tone-bg: ${config.bg};">${config.text}</span>
          `;
        }

        // Mettre à jour le footer si nécessaire (masquer/afficher le bouton Clôturer)
        const footer = panel.querySelector('.details-panel-footer');
        if (footer && updatedRequest.status === 'completed') {
          const closeBtn = footer.querySelector('.cmd-center-btn-primary');
          if (closeBtn) {
            closeBtn.remove();
          }
        } else if (footer && updatedRequest.status !== 'completed') {
          const existingCloseBtn = footer.querySelector('.cmd-center-btn-primary');
          if (!existingCloseBtn) {
            const closeRequestBtn = document.createElement('button');
            closeRequestBtn.className = 'cmd-center-btn-primary';
            closeRequestBtn.style.cssText = 'display: flex; align-items: center; gap: 0.5rem;';
            closeRequestBtn.innerHTML = `
              <span class="material-symbols-outlined" style="font-size: 18px;">check_circle</span>
              <span>Clôturer</span>
            `;
            closeRequestBtn.addEventListener('click', async () => {
              await handleCloseRequest();
            });
            // Insérer avant le bouton "Changer le statut"
            const changeStatusBtn = footer.querySelector('.cmd-center-btn-secondary');
            if (changeStatusBtn) {
              footer.insertBefore(closeRequestBtn, changeStatusBtn);
            } else {
              footer.appendChild(closeRequestBtn);
            }
          }
        }

        // Callback pour recharger la liste si fourni
        if (onRequestUpdated && typeof onRequestUpdated === 'function') {
          onRequestUpdated();
        }

        closeModal();

        if (typeof window.showSuccessModal === 'function') {
          window.showSuccessModal('Le statut de la demande a été mis à jour avec succès.', 'Statut mis à jour');
        }
      } catch (error) {
        console.error('Error updating status', error);
        if (typeof window.showGlobalNotification === 'function') {
          window.showGlobalNotification('error', error.message || 'Erreur lors de la mise à jour du statut');
        } else {
          alert(error.message || 'Erreur lors de la mise à jour du statut');
        }
        confirmBtn.disabled = false;
        confirmBtn.innerHTML = originalHtml;
      }
    });

    // Fermer avec Escape
    const escapeHandler = (e) => {
      if (e.key === 'Escape') {
        closeModal();
        document.removeEventListener('keydown', escapeHandler);
      }
    };
    document.addEventListener('keydown', escapeHandler);
  }

  // Ajouter les styles pour les boutons
  const style = document.createElement('style');
  style.textContent = `
    .cmd-center-btn-secondary {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      background: linear-gradient(135deg, #508C94, #3A6B70);
      border: none;
      color: var(--text-primary);
      padding: 0.625rem 1.5rem;
      font-size: 0.875rem;
      font-family: 'Barlow Condensed', sans-serif;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      cursor: pointer;
      transition: all 0.3s ease;
      box-shadow: 0 0 15px rgba(80, 140, 148, 0.3);
      border-radius: 0;
    }
    .cmd-center-btn-secondary:hover {
      box-shadow: 0 0 25px rgba(80, 140, 148, 0.6);
      transform: translateY(-1px);
      background: linear-gradient(135deg, #3A6B70, #2A4F54);
    }
    .cmd-center-btn-secondary:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .cmd-center-btn-danger {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      background: linear-gradient(135deg, #ef4444, #dc2626);
      border: none;
      color: #fef2f2;
      padding: 0.625rem 1.5rem;
      font-size: 0.875rem;
      font-family: 'Barlow Condensed', sans-serif;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      cursor: pointer;
      transition: all 0.3s ease;
      box-shadow: 0 0 15px rgba(239, 68, 68, 0.3);
      border-radius: 0;
    }
    .cmd-center-btn-danger:hover {
      box-shadow: 0 0 25px rgba(239, 68, 68, 0.6);
      transform: translateY(-1px);
      background: linear-gradient(135deg, #dc2626, #b91c1c);
    }
    .cmd-center-btn-danger:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `;
  document.head.appendChild(style);

  return container;
}

